"use client";

import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";

import { AppDispatch } from "../../../../../../app/store";
import { RootState } from "../../../../../../app/rootReducer";
import { fetchCompanyProfileAction } from "../../slices/companyProfileSlice";
import { AddressChangeRequestSection } from "../AddressChangeRequestSection/AddressChangeRequestSection";

import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";

interface ReadOnlyFieldProps {
  label: string;
  value?: string | null;
}

const ReadOnlyField: React.FC<ReadOnlyFieldProps> = ({ label, value }) => (
  <div>
    <p className="text-sm font-normal text-gray-500 dark:text-gray-400">
      {label}
    </p>
    <p className="mt-1 text-sm text-gray-900 dark:text-white">
      {value && value.trim() !== "" ? value : "—"}
    </p>
  </div>
);

const AddressSection: React.FC<{
  title: string;
  addressLine1?: string | null;
  city?: string | null;
  state?: string | null;
  zipCode?: string | null;
  country?: string | null;
}> = ({ title, addressLine1, city, state, zipCode, country }) => (
  <div className="space-y-4">
    <h3 className="text-base font-normal text-gray-900 dark:text-white">
      {title}
    </h3>
    <ReadOnlyField label="Street Address" value={addressLine1} />
    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
      <ReadOnlyField label="City" value={city} />
      <ReadOnlyField label="State / Province" value={state} />
    </div>
    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
      <ReadOnlyField label="ZIP / Postal Code" value={zipCode} />
      <ReadOnlyField label="Country" value={country} />
    </div>
  </div>
);

const CompanyProfile: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();

  const { companyProfile } = useSelector(
    (state: RootState) => state.companyProfile,
  );

  useEffect(() => {
    dispatch(fetchCompanyProfileAction());
  }, [dispatch]);

  const shippingSameAsBilling = companyProfile?.isMailingAddressSame ?? true;

  return (
    <Card>
      <CardHeader>
        <CardTitle>Company Profile</CardTitle>
        <CardDescription>
          Your company information on file. To change your address, send us a
          request below.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="space-y-6">
          <ReadOnlyField
            label="Company Name"
            value={companyProfile?.companyName}
          />
          <ReadOnlyField
            label="Company Email"
            value={companyProfile?.companyEmail}
          />
          <ReadOnlyField
            label="Website URL"
            value={companyProfile?.websiteUrl}
          />

          <div className="pt-6 border-t border-gray-200 dark:border-slate-700">
            <AddressSection
              addressLine1={companyProfile?.addressLine1}
              city={companyProfile?.city}
              country={companyProfile?.country}
              state={companyProfile?.state}
              title="Billing Address"
              zipCode={companyProfile?.zipCode}
            />
          </div>

          <div className="pt-6 border-t border-gray-200 dark:border-slate-700">
            {shippingSameAsBilling ? (
              <div className="space-y-4">
                <h3 className="text-base font-normal text-gray-900 dark:text-white">
                  Shipping Address
                </h3>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Same as billing address
                </p>
              </div>
            ) : (
              <AddressSection
                addressLine1={companyProfile?.mailingAddressLine1}
                city={companyProfile?.mailingCity}
                country={companyProfile?.mailingCountry}
                state={companyProfile?.mailingState}
                title="Shipping Address"
                zipCode={companyProfile?.mailingZipCode}
              />
            )}
          </div>

          <div className="pt-6 border-t border-gray-200 dark:border-slate-700">
            <AddressChangeRequestSection />
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default CompanyProfile;
